// The center tower and the barracks (2026-10-26, graphics pass "more realistic"; moved here from main.ts). Built in
// code until the generated models come (GRAPHICS-PASS.md D): carved stone (look.ts stoneMaterial) with the shapes
// that make a toy castle read, a plinth, string courses, battlements, arched windows lit warm from inside (bright
// enough for Best's bloom) and a wooden door. Each building's stone is one merged mesh, so the detail costs geometry,
// not draws.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { radialTexture, shared, stoneMaterial, weatheredMaterial } from './look';
import { PALETTE } from './palette';

/** A building's trim in one draw (Light has to stay as fast as main, and a draw is most of a frame's cost there):
 *  window reveals and the door lit as wood and dark stone, the window glass glowing from inside, over 1 in the HDR
 *  target so Best's bloom takes it. Which is which rides on aPart; the colors are the palette's own (palette.ts). */
const TRIM = { frame: 0, door: 1, glow: 2, lamp: 3 } as const;
const trimUniforms = { uFrame: { value: PALETTE.trim.frame }, uDoor: { value: PALETTE.trim.door }, uGlow: { value: PALETTE.glow }, uLamp: { value: PALETTE.lamp } };
let trimMat: THREE.MeshLambertMaterial | null = null;
function trimMaterial() {
  if (trimMat) return trimMat;
  const m = new THREE.MeshLambertMaterial();
  m.userData.uniforms = trimUniforms;
  m.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, trimUniforms);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aPart; varying float vPart;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vPart = aPart;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vPart; uniform vec3 uFrame, uDoor, uGlow, uLamp;')
      .replace('#include <color_fragment>', '#include <color_fragment>\n  diffuseColor.rgb = vPart < 0.5 ? uFrame : vPart < 1.5 ? uDoor : vec3(0.0);')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n  if (vPart > 2.5) totalEmissiveRadiance = uLamp; else if (vPart > 1.5) totalEmissiveRadiance = uGlow;');
  };
  m.customProgramCacheKey = () => 'trim';
  return (trimMat = m);
}

/** An arched opening (a window or a door) w wide and h tall, its foot at y 0, facing +z. */
function arch(w: number, h: number) {
  const s = new THREE.Shape();
  s.moveTo(-w / 2, 0); s.lineTo(w / 2, 0); s.lineTo(w / 2, h - w / 2);
  s.absarc(0, h - w / 2, w / 2, 0, Math.PI, false);
  s.lineTo(-w / 2, 0);
  return new THREE.ShapeGeometry(s, 8).deleteAttribute('uv');
}

/** Pieces of one material, each placed by a matrix, merged into one mesh; `part` tags a trim piece (TRIM). */
class Batch {
  parts: THREE.BufferGeometry[] = [];
  add(g: THREE.BufferGeometry, m: THREE.Matrix4, part?: number) {
    const n = g.index ? g.toNonIndexed() : g.clone(); if (n.attributes.uv) n.deleteAttribute('uv');
    if (part !== undefined) n.setAttribute('aPart', new THREE.Float32BufferAttribute(new Float32Array(n.attributes.position.count).fill(part), 1));
    this.parts.push(n.applyMatrix4(m));
  }
  mesh(mat: THREE.Material) { return new THREE.Mesh(mergeGeometries(this.parts)!, mat); }
}
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _p = new THREE.Vector3(), _s = new THREE.Vector3(1, 1, 1), _up = new THREE.Vector3(0, 1, 0);
/** Placed at x, y, z, turned by ry about the vertical. */
const place = (x: number, y: number, z: number, ry = 0) => _m.compose(_p.set(x, y, z), _q.setFromAxisAngle(_up, ry), _s);
/** On a round wall of radius r at angle a (radians round from +x toward +z), height y, facing out. */
const onRound = (r: number, a: number, y: number) => place(Math.cos(a) * r, y, Math.sin(a) * r, Math.PI / 2 - a);

/** The center: a round stone tower on a plinth, string courses, a corbelled parapet with battlements, a slate cone
 *  roof with a gilded finial, arched windows lit warm in two staggered rows, and a door facing the default camera. */
export function buildCenter(x: number, z: number) {
  const g = new THREE.Group(), stone = new Batch(), trim = new Batch();
  const R = 3.45; // the shaft's radius at mid height
  stone.add(new THREE.CylinderGeometry(4.3, 4.6, 0.7, 36), place(0, 0.35, 0));
  stone.add(new THREE.CylinderGeometry(R, R, 6.4, 36, 1, true), place(0, 0.7 + 3.2, 0));
  for (const y of [0.85, 4.1]) stone.add(new THREE.CylinderGeometry(R + 0.12, R + 0.12, 0.22, 36), place(0, y, 0));
  // the corbels flaring out to carry the parapet, then the parapet: a ring with its walk on top
  stone.add(new THREE.CylinderGeometry(3.9, R, 0.6, 36, 1, true), place(0, 7.4, 0));
  stone.add(new THREE.LatheGeometry([[3.3, 7.7], [3.9, 7.7], [3.9, 8.45], [3.3, 8.45], [3.3, 7.7]].map(([a, b]) => new THREE.Vector2(a, b)), 36), place(0, 0, 0));
  for (let k = 0; k < 14; k++) stone.add(new THREE.BoxGeometry(0.95, 0.6, 0.55), onRound(3.62, (k / 14) * Math.PI * 2, 8.75));
  // windows: a dark reveal and the glow inside it, two rows a half step apart round the tower
  for (let k = 0; k < 8; k++) {
    const row = k >> 2, a = ((k & 3) * Math.PI) / 2 + (row ? 0 : Math.PI / 4), y = row ? 5.0 : 2.3; // the lower row clears the door
    trim.add(arch(0.95, 1.6), onRound(R + 0.02, a, y - 0.12), TRIM.frame);
    trim.add(arch(0.66, 1.3), onRound(R + 0.05, a, y), TRIM.glow);
  }
  trim.add(arch(1.9, 2.75), onRound(R + 0.02, Math.PI / 2, 0.7), TRIM.frame);
  trim.add(arch(1.55, 2.5), onRound(R + 0.05, Math.PI / 2, 0.7), TRIM.door);
  const roof = new THREE.Mesh(new THREE.ConeGeometry(3.15, 3.6, 36), weatheredMaterial(Object.assign(new THREE.MeshStandardMaterial({ roughness: 0.42, metalness: 0 }), { color: PALETTE.trim.slate }), 'roof'));
  roof.position.y = 8.45 + 1.8;
  const finial = new THREE.Mesh(new THREE.SphereGeometry(0.28, 12, 8), Object.assign(new THREE.MeshStandardMaterial({ roughness: 0.3, metalness: 0.8 }), { color: PALETTE.trim.gold }));
  finial.position.y = 8.45 + 3.6 + 0.15;
  const top = new THREE.Group(); top.name = 'roof'; top.add(roof, finial); // main.ts lifts it away while the giant stands there
  g.add(stone.mesh(stoneMaterial(PALETTE.stone.tower, { round: { x, z, r: R } })), trim.mesh(trimMaterial()), top);
  g.position.set(x, 0, z);
  return shade(g);
}

/** The barracks' stone, shared by every floor of every barracks. */
let baseStone: THREE.Material | null = null;

export type BaseView = { g: THREE.Group; floors: THREE.Mesh[]; bar: THREE.Mesh; barBg: THREE.Mesh; flag: THREE.Mesh };
/** A barracks: a stone keep on a plinth with corner buttresses, battlements round its top, a door and lit windows; a
 *  floor per tier stacked on it (main.ts shows them by tier and lifts the roof and flag), a glossy roof and a flag in
 *  the team's color, and a health bar. */
export function buildBase(x: number, z: number, color: number): BaseView {
  baseStone ??= stoneMaterial(PALETTE.stone.keep);
  const g = new THREE.Group(), stone = new Batch(), trim = new Batch();
  stone.add(new THREE.BoxGeometry(6.7, 0.4, 6.7), place(0, 0.2, 0));
  stone.add(new THREE.BoxGeometry(6, 2.4, 6), place(0, 1.2, 0));
  for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) stone.add(new THREE.BoxGeometry(1.0, 2.75, 1.0), place(sx * 2.9, 1.375, sz * 2.9));
  // battlements: merlons round the top edge, a gap between each
  for (let k = 0; k < 4; k++) for (let n = 0; n < 4; n++) {
    const t = -2.0 + n * 1.33, ry = (k * Math.PI) / 2, cx = Math.cos(ry), sz = Math.sin(ry);
    stone.add(new THREE.BoxGeometry(0.7, 0.55, 0.4), place(cx * 2.82 - sz * t, 2.67, sz * 2.82 + cx * t, ry + Math.PI / 2));
  }
  // the door faces +z (the default camera), a lit window either side, and two on each side wall
  trim.add(arch(1.7, 1.95), place(0, 0.4, 3.02), TRIM.frame); trim.add(arch(1.35, 1.75), place(0, 0.4, 3.04), TRIM.door);
  for (const [wx, wz, ry] of [[-1.8, 3.02, 0], [1.8, 3.02, 0], [3.02, -1.2, Math.PI / 2], [3.02, 1.2, Math.PI / 2], [-3.02, -1.2, -Math.PI / 2], [-3.02, 1.2, -Math.PI / 2], [-1.2, -3.02, Math.PI], [1.2, -3.02, Math.PI]]) {
    const ox = Math.sin(ry) * 0.02, oz = Math.cos(ry) * 0.02;
    trim.add(arch(0.7, 1.05), place(wx, 1.0, wz, ry), TRIM.frame); trim.add(arch(0.48, 0.85), place(wx + ox, 1.08, wz + oz, ry), TRIM.glow);
  }
  g.add(stone.mesh(baseStone), trim.mesh(trimMaterial()));
  const floors: THREE.Mesh[] = [];
  for (let k = 0; k < 4; k++) {
    const f = new THREE.Mesh(new THREE.BoxGeometry(4.2 - k * 0.6, 1.4, 4.2 - k * 0.6), baseStone);
    f.position.y = 3.1 + k * 1.4; f.visible = false; g.add(f); floors.push(f);
  }
  const roof = new THREE.Mesh(new THREE.ConeGeometry(3.2, 2.2, 4), weatheredMaterial(new THREE.MeshStandardMaterial({ color, roughness: 0.3, metalness: 0 }), 'team-roof', 0.25)); // a dusting: the team's color reads in snow
  roof.rotation.y = Math.PI / 4; roof.name = 'roof'; g.add(roof);
  const flag = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1), new THREE.MeshLambertMaterial({ color, side: THREE.DoubleSide }));
  g.add(flag);
  shade(g); // before the bars, which take no shadow
  const barBg = new THREE.Mesh(new THREE.PlaneGeometry(6, 0.5), new THREE.MeshBasicMaterial({ color: 0x222222, depthTest: false }));
  const bar = new THREE.Mesh(new THREE.PlaneGeometry(6, 0.5), new THREE.MeshBasicMaterial({ color, depthTest: false }));
  barBg.renderOrder = bar.renderOrder = 10;
  g.add(barBg, bar);
  g.position.set(x, 0, z);
  return { g, floors, bar, barBg, flag };
}

/** The lanterns along the roads (2026-10-26, seasons and time of day; board.ts lanternSpots places them): an iron post
 *  with a glass lamp box under a little roof, all of them one instanced draw in the trim material (the glass is its
 *  lamp part, PALETTE.lamp, which sky.ts lights from dusk), and under each a warm pool of light on the ground (one
 *  additive draw, its strength shared.uLamps; main.ts shows it only while the lights are on). About 3 m tall, half again
 *  a soldier. */
export function buildLanterns(spots: { x: number; z: number; ry: number }[]) {
  const parts = new Batch(), at = (x: number, y: number, z: number) => place(x, y, z);
  parts.add(new THREE.CylinderGeometry(0.16, 0.2, 0.25, 8), at(0, 0.12, 0), TRIM.frame);
  parts.add(new THREE.CylinderGeometry(0.06, 0.08, 2.5, 6), at(0, 1.45, 0), TRIM.frame);
  parts.add(new THREE.BoxGeometry(0.42, 0.08, 0.42), at(0, 2.72, 0), TRIM.frame);
  parts.add(new THREE.BoxGeometry(0.34, 0.5, 0.34), at(0, 3.0, 0), TRIM.lamp);
  for (const [cx, cz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) parts.add(new THREE.BoxGeometry(0.05, 0.52, 0.05), at(cx * 0.18, 3.0, cz * 0.18), TRIM.frame);
  parts.add(new THREE.ConeGeometry(0.34, 0.3, 4).rotateY(Math.PI / 4), at(0, 3.4, 0), TRIM.frame);
  parts.add(new THREE.SphereGeometry(0.06, 6, 4), at(0, 3.58, 0), TRIM.frame);
  const geo = mergeGeometries(parts.parts)!;
  const posts = new THREE.InstancedMesh(geo, trimMaterial(), Math.max(1, spots.length));
  const pools = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), poolMaterial(), Math.max(1, spots.length));
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), one = new THREE.Vector3(1, 1, 1), pool = new THREE.Vector3(8, 1, 8);
  spots.forEach((o, k) => {
    posts.setMatrixAt(k, m.compose(p.set(o.x, 0, o.z), q.setFromAxisAngle(_up, o.ry), one));
    pools.setMatrixAt(k, m.compose(p.set(o.x, 0.04, o.z), q.identity(), pool));
  });
  posts.count = pools.count = spots.length;
  posts.receiveShadow = true; // no shadow of its own: a 2 m post's is a few pixels from the match camera and costs a draw on every tier
  pools.renderOrder = 1; pools.visible = false; pools.userData.warm = true;
  return { posts, pools };
}
/** A lantern's light on the ground: a soft warm round, added to what is there, as strong as the lights are on. */
function poolMaterial() {
  const m = new THREE.MeshBasicMaterial({ color: new THREE.Color(1.0, 0.45, 0.12), alphaMap: radialTexture(), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uLamps = shared.uLamps;
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uLamps;')
      .replace('#include <alphamap_fragment>', '#include <alphamap_fragment>\n  diffuseColor.a *= 0.5 * uLamps;');
  };
  m.customProgramCacheKey = () => 'lamp-pool';
  return m;
}

/** In the sun's shadow pass and taking shadows (a group's meshes; the glass's glow is emissive, so a shadow on it
 *  doesn't dim it). */
function shade<T extends THREE.Object3D>(o: T) {
  o.traverse((m) => { m.castShadow = m.receiveShadow = true; });
  return o;
}
